import { makeAutoObservable, reaction, runInAction } from "mobx";
import { fetchPropertiesFromApi, fetchSchemaFromApi } from "./propertyCore/propertyApi";
import { IS_DEV } from "../utils/env";
import {
  cachedPropertyIdMatchesKey,
  getCacheAge,
  isCacheFresh,
  listCacheKey,
  mergePropertiesById,
  mergePropertyPatch,
  resolveListTextSearchQuery,
} from "./propertyCore/propertyCacheUtils";
import { logPropertyFilter } from "@/utils/propertyFilterDebug";

/** Max automatic "next page" fetches while client filters match zero rows (product cap; resets when filter context changes). */
const QUERY_DRIVEN_AUTO_PAGE_MAX = 5;

class PropertyListStore {
  listCustomer = null;
  listProperties = [];
  listPagination = null;
  listPropertyIds = [];
  listLoading = false;
  listLoadingMore = false;
  listError = null;
  listTextSearchQuery = "";
  /** Query that {@link listProperties} currently belongs to (set on cache apply / live apply only). */
  listPropertiesSearchQuery = "";
  /** First-page fetch in flight for {@link listTextSearchQuery}; does not imply the grid was cleared. */
  listSearchRefreshing = false;
  /** `true` / `false` after a full-list first page; `null` until known. Search pages do not update this. */
  listAccountHasProperties = null;

  listCacheByKey = new Map();
  defaultListRevalidateAfterMs = 2 * 60 * 1000;

  schemaCustomer = null;
  schema = null;
  schemaLoading = false;
  schemaError = null;

  _inFlightListKey = null;
  _inFlightListByCacheKey = new Map();
  _inFlightSchemaCustomer = null;
  _resetGeneration = 0;
  /** Observable epoch bumped on identity/customer reset so mounted PropertyList refetches after cancelled in-flight loads. */
  listResetEpoch = 0;

  /** Consecutive auto-pagination fetches for the current filter context (non-observable). */
  _queryDrivenConsecutiveAutoFetches = 0;

  /** Avoid repeating the auto-pagination stop log for the same filter context (non-observable). */
  _queryDrivenLoggedStop = false;

  /** Last query+filterSignature key from {@link syncQueryDrivenAutoPaginationContext} (non-observable). */
  _queryDrivenLastContextKey = "";

  /** @type {(() => void) | null} */
  _queryDrivenPaginationDisposer = null;

  /** Debounced query + filtered count synced from PropertyList; drives auto-pagination reaction. */
  queryDrivenSearchQuery = "";
  queryDrivenFilterSignature = "";
  queryDrivenHasActiveFilters = false;
  queryDrivenFilteredCount = 0;
  queryDrivenLoadRole = null;
  queryDrivenLoadUserId = null;
  /** @type {number | undefined} */
  queryDrivenRevalidateAfterMs = undefined;
  /** True when auto-pagination hit {@link QUERY_DRIVEN_AUTO_PAGE_MAX} for the current filter context. */
  queryDrivenAutoPaginationExhausted = false;

  constructor() {
    makeAutoObservable(
      this,
      {
        _queryDrivenPaginationDisposer: false,
        _queryDrivenConsecutiveAutoFetches: false,
        _queryDrivenLoggedStop: false,
        _queryDrivenLastContextKey: false,
      },
      { autoBind: true }
    );
  }

  _getPropertyCreatedAtMs(property) {
    const raw = property?.created_at || property?.insert_date || null;
    if (!raw) return 0;

    const timestamp = new Date(raw).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  _sortPropertiesNewestFirst(properties = []) {
    if (!Array.isArray(properties) || properties.length <= 1) return properties;
    return [...properties].sort((a, b) => this._getPropertyCreatedAtMs(b) - this._getPropertyCreatedAtMs(a));
  }

  resetList() {
    this.listCustomer = null;
    this.listProperties = [];
    this.listPagination = null;
    this.listPropertyIds = [];
    this.listLoading = false;
    this.listLoadingMore = false;
    this.listError = null;
    this.listTextSearchQuery = "";
    this.listPropertiesSearchQuery = "";
    this.listSearchRefreshing = false;
    this.listAccountHasProperties = null;
    this._inFlightListKey = null;
    this.queryDrivenSearchQuery = "";
    this.queryDrivenFilterSignature = "";
    this.queryDrivenHasActiveFilters = false;
    this.queryDrivenFilteredCount = 0;
    this.queryDrivenLoadRole = null;
    this.queryDrivenLoadUserId = null;
    this.queryDrivenRevalidateAfterMs = undefined;
    this.queryDrivenAutoPaginationExhausted = false;
  }

  resetSchema() {
    this.schemaCustomer = null;
    this.schema = null;
    this.schemaLoading = false;
    this.schemaError = null;
    this._inFlightSchemaCustomer = null;
  }

  _clearAll() {
    this._resetGeneration += 1;
    this.listResetEpoch += 1;
    this.resetList();
    this.resetSchema();
    this.listCacheByKey.clear();
    this._inFlightListByCacheKey.clear();
  }

  reset() {
    this._clearAll();
  }

  resetForNewCustomer() {
    this._clearAll();
  }

  invalidateListCache({ customer = null } = {}) {
    if (!customer) {
      this.listCacheByKey.clear();
      return;
    }

    const prefix = `${customer}::`;
    for (const key of this.listCacheByKey.keys()) {
      if (key.startsWith(prefix)) this.listCacheByKey.delete(key);
    }
  }

  _paginationHasNext(pagination) {
    return pagination?.has_next === true || pagination?.has_next === "true";
  }

  _recordAccountHasPropertiesFromFullListFirstPage(properties, pagination) {
    const count = Array.isArray(properties) ? properties.length : 0;
    this.listAccountHasProperties = count > 0 || this._paginationHasNext(pagination);
  }

  _applyCachedList(cacheEntry, customer, normalizedQuery = "") {
    runInAction(() => {
      this.listCustomer = customer;
      this.listProperties = cacheEntry.properties || [];
      this.listPagination = cacheEntry.pagination || null;
      this.listPropertyIds = cacheEntry.propertyIds || [];
      this.listError = null;
      this.listLoading = false;
      this.listLoadingMore = false;
      this.listPropertiesSearchQuery = normalizedQuery;
      this.listSearchRefreshing = false;
      if (normalizedQuery === "") {
        this._recordAccountHasPropertiesFromFullListFirstPage(cacheEntry.properties, cacheEntry.pagination);
      }
    });
  }

  async loadSchema(customer) {
    if (!customer) return null;
    if (this.schemaCustomer === customer && this.schema) return this.schema;
    if (this._inFlightSchemaCustomer === customer) return this.schema;

    const generationAtStart = this._resetGeneration;
    runInAction(() => {
      this.schemaLoading = true;
      this.schemaError = null;
      this._inFlightSchemaCustomer = customer;
    });

    try {
      const parsed = await fetchSchemaFromApi(customer);
      if (this._resetGeneration !== generationAtStart) return null;

      runInAction(() => {
        this.schemaCustomer = customer;
        this.schema = parsed;
      });
      return parsed;
    } catch {
      if (this._resetGeneration !== generationAtStart) return null;
      runInAction(() => {
        this.schemaError = "Error loading schema";
      });
      return null;
    } finally {
      if (this._resetGeneration === generationAtStart) {
        runInAction(() => {
          this.schemaLoading = false;
          this._inFlightSchemaCustomer = null;
        });
      }
    }
  }

  /**
   * Load or paginate the property list. `role` and `userId` are forwarded to {@link fetchPropertiesFromApi}
   * (GraphQL `user_role` / `user_id` when set); `role` also gates server text search.
   * `textSearchQuery` omitted inherits {@link listTextSearchQuery}; explicit `""` fetches the full list.
   * For a stale first-page refetch of the **same** search with `reset: false` (SWR / background sync),
   * new data is merged by property id so paginated rows are not dropped.
   *
   * @param {{ customer?: string, nextPage?: unknown, reset?: boolean, revalidateAfterMs?: number|null, role?: unknown, userId?: string|null, textSearchQuery?: string }} [params]
   * @returns {Promise<null|object>}
   */
  async loadList({
    customer,
    nextPage = null,
    reset = false,
    revalidateAfterMs = null,
    role = null,
    userId = null,
    textSearchQuery,
  } = {}) {
    if (!customer) return null;

    const normalizedQuery = resolveListTextSearchQuery({
      role,
      textSearchQuery,
      current: this.listTextSearchQuery,
    });
    const apiTextSearchQuery =
      normalizedQuery === ""
        ? ""
        : textSearchQuery !== undefined
          ? String(textSearchQuery).trim()
          : this.listTextSearchQuery;
    const searchContextChanged = normalizedQuery !== this.listTextSearchQuery;

    let normalizedNextPage = nextPage === null || nextPage === undefined || nextPage === "" ? null : nextPage;
    if (searchContextChanged) {
      normalizedNextPage = null;
      runInAction(() => {
        this.listTextSearchQuery = normalizedQuery;
        this.listPropertyIds = [];
        this.listPagination = null;
      });
    }

    const cacheKey = listCacheKey(customer, userId, role, normalizedQuery);
    const cacheEntry = this.listCacheByKey.get(cacheKey) || null;
    const cacheAgeMs = cacheEntry ? getCacheAge(cacheEntry.fetchedAt) : null;

    if (searchContextChanged && !cacheEntry && normalizedNextPage === null) {
      runInAction(() => {
        this.listSearchRefreshing = true;
      });
    }

    if (normalizedNextPage === null && cacheEntry && !reset) {
      this._applyCachedList(cacheEntry, customer, normalizedQuery);
      const cachedResult = {
        data: cacheEntry.properties || [],
        pagination: cacheEntry.pagination || null,
        ids: cacheEntry.propertyIds || [],
        statusCode: "cached",
      };

      if (isCacheFresh(cacheAgeMs, revalidateAfterMs, this.defaultListRevalidateAfterMs)) {
        return cachedResult;
      }

      runInAction(() => {
        this.listSearchRefreshing = true;
      });
    }

    if (normalizedNextPage === null) {
      const existingPromise = this._inFlightListByCacheKey.get(cacheKey);
      if (existingPromise) {
        const existingResult = await existingPromise;
        if (existingResult != null) return existingResult;
      }
    }

    const listKey = JSON.stringify({
      customer,
      userId: userId ?? null,
      role: role ?? null,
      nextPage: normalizedNextPage,
      propertyIds: this.listPropertyIds,
      reset,
      textSearchQuery: normalizedQuery,
    });
    if (this._inFlightListKey === listKey) return null;

    const generationAtStart = this._resetGeneration;
    const replaceLiveList = normalizedNextPage === null && (reset || (searchContextChanged && !cacheEntry));
    const runFetch = async () => {
      runInAction(() => {
        this.listCustomer = customer;
        this.listError = null;
        this._inFlightListKey = listKey;

        if (normalizedNextPage === null) {
          const isEmptyLiveList = !Array.isArray(this.listProperties) || this.listProperties.length === 0;
          this.listLoading = reset === true || isEmptyLiveList;
          this.listSearchRefreshing = true;
        } else {
          this.listLoadingMore = true;
        }

        if (reset) {
          this.listPropertyIds = [];
          this.listPagination = null;
        }
      });

      try {
        const result = await fetchPropertiesFromApi({
          customer,
          nextPage: normalizedNextPage,
          propertyIds: this.listPropertyIds,
          limit: 100,
          role,
          userId,
          textSearchQuery: apiTextSearchQuery,
        });

        if (this._resetGeneration !== generationAtStart) return null;

        const applyToLiveList = this.listTextSearchQuery === normalizedQuery;
        const fetchedProperties = this._sortPropertiesNewestFirst(Array.isArray(result.data) ? result.data : []);
        const fetchedIds = result.ids && typeof result.ids === "object" ? Object.keys(result.ids) : [];
        const shouldCacheFirstPage = normalizedNextPage === null && (fetchedProperties.length > 0 || normalizedQuery !== "");

        if (result.isEmpty) {
          if (IS_DEV) {
            console.log("[PropertyListStore][debug] loadList empty result", {
              context: {
                role: role || "unknown",
                userId: userId || "unknown",
                customer: customer || "unknown",
                textSearchQuery: normalizedQuery || "__none__",
              },
              statusCode: 204,
              resultCount: 0,
            });
          }

          const softRevalidateFirstPage = normalizedNextPage === null && !replaceLiveList;
          if (shouldCacheFirstPage) {
            runInAction(() => {
              this.listCacheByKey.set(cacheKey, {
                properties: [],
                pagination: result.pagination || null,
                propertyIds: [],
                fetchedAt: Date.now(),
              });
            });
          }

          if (applyToLiveList) {
            runInAction(() => {
              if (normalizedNextPage === null || reset) {
                if (softRevalidateFirstPage) {
                  this.listPagination = result.pagination || {
                    ...(this.listPagination || {}),
                    has_next: false,
                    next_page: null,
                  };
                } else {
                  this.listProperties = [];
                  this.listPagination = null;
                  this.listPropertyIds = [];
                }
              } else {
                this.listPagination = result.pagination || {
                  ...(this.listPagination || {}),
                  has_next: false,
                  next_page: null,
                };
              }
              if (normalizedNextPage === null && normalizedQuery === "") {
                this._recordAccountHasPropertiesFromFullListFirstPage(this.listProperties, this.listPagination);
              }
              if (normalizedNextPage === null) {
                this.listPropertiesSearchQuery = normalizedQuery;
              }
            });
          }

          return {
            data: [],
            pagination:
              applyToLiveList && softRevalidateFirstPage
                ? this.listPagination
                : applyToLiveList && (normalizedNextPage === null || reset)
                  ? null
                  : applyToLiveList
                    ? this.listPagination
                    : result.pagination || null,
            ids:
              applyToLiveList && softRevalidateFirstPage
                ? this.listPropertyIds
                : applyToLiveList && (normalizedNextPage === null || reset)
                  ? []
                  : applyToLiveList
                    ? this.listPropertyIds
                    : [],
            statusCode: 204,
          };
        }

        runInAction(() => {
          if (shouldCacheFirstPage) {
            this.listCacheByKey.set(cacheKey, {
              properties: fetchedProperties,
              pagination: result.pagination,
              propertyIds: fetchedIds.length > 0 ? fetchedIds : fetchedProperties.map((p) => String(p.id)).filter(Boolean),
              fetchedAt: Date.now(),
            });
          }
        });

        if (applyToLiveList) {
          runInAction(() => {
            if (normalizedNextPage === null || reset) {
              if (normalizedNextPage === null && !replaceLiveList) {
                this.listProperties = this._sortPropertiesNewestFirst(mergePropertiesById(this.listProperties, result.data));
              } else {
                this.listProperties = fetchedProperties;
              }
            } else {
              this.listProperties = this._sortPropertiesNewestFirst(mergePropertiesById(this.listProperties, result.data));
            }

            if (normalizedNextPage === null && !replaceLiveList) {
              this.listPropertyIds = this.listProperties
                .map((p) => (p?.id != null && p?.id !== "" ? String(p.id) : null))
                .filter(Boolean);
            } else if (normalizedNextPage === null) {
              this.listPropertyIds =
                fetchedIds.length > 0
                  ? fetchedIds
                  : this.listProperties.map((p) => (p?.id != null && p?.id !== "" ? String(p.id) : null)).filter(Boolean);
            } else {
              this.listPropertyIds =
                fetchedIds.length > 0
                  ? fetchedIds
                  : this.listProperties.map((p) => (p?.id != null && p?.id !== "" ? String(p.id) : null)).filter(Boolean);
            }
            this.listPagination = result.pagination;

            if (normalizedNextPage === null && normalizedQuery === "") {
              this._recordAccountHasPropertiesFromFullListFirstPage(this.listProperties, this.listPagination);
            }

            if (normalizedNextPage === null) {
              this.listPropertiesSearchQuery = normalizedQuery;
            }

            if (shouldCacheFirstPage || normalizedNextPage !== null) {
              this.listCacheByKey.set(cacheKey, {
                properties: this.listProperties,
                pagination: this.listPagination,
                propertyIds: this.listPropertyIds,
                fetchedAt: Date.now(),
              });
            }
          });
        } else if (normalizedNextPage !== null) {
          runInAction(() => {
            const existing = this.listCacheByKey.get(cacheKey);
            if (!existing || !Array.isArray(existing.properties)) return;

            const mergedProperties = this._sortPropertiesNewestFirst(
              mergePropertiesById(existing.properties, fetchedProperties)
            );
            const mergedIds =
              fetchedIds.length > 0
                ? [...new Set([...(existing.propertyIds || []), ...fetchedIds])]
                : mergedProperties.map((p) => (p?.id != null && p?.id !== "" ? String(p.id) : null)).filter(Boolean);

            this.listCacheByKey.set(cacheKey, {
              ...existing,
              properties: mergedProperties,
              pagination: result.pagination,
              propertyIds: mergedIds,
              fetchedAt: Date.now(),
            });
          });
        }

        if (IS_DEV) {
          console.log("[PropertyListStore][debug] loadList success", {
            context: {
              role: role || "unknown",
              userId: userId || "unknown",
              customer: customer || "unknown",
              textSearchQuery: normalizedQuery || "__none__",
            },
            statusCode: result.statusCode ?? null,
            resultCount: Array.isArray(result.data) ? result.data.length : 0,
            storeCount: applyToLiveList ? this.listProperties.length : undefined,
          });
        }

        return {
          data: result.data,
          pagination: applyToLiveList ? this.listPagination : result.pagination,
          ids: applyToLiveList ? this.listPropertyIds : fetchedIds,
          statusCode: result.statusCode,
        };
      } catch {
        if (this._resetGeneration !== generationAtStart) return null;
        runInAction(() => {
          if (this.listTextSearchQuery === normalizedQuery) {
            this.listError = "Error loading properties";
            if (normalizedNextPage === null && reset) {
              this.listProperties = [];
            }
          }
        });
        return null;
      } finally {
        runInAction(() => {
          if (this._inFlightListKey === listKey) {
            this._inFlightListKey = null;
          }
          if (this._resetGeneration === generationAtStart && this.listTextSearchQuery === normalizedQuery) {
            this.listLoading = false;
            this.listLoadingMore = false;
            if (normalizedNextPage === null) {
              this.listSearchRefreshing = false;
            }
          }
        });
      }
    };

    const promise = runFetch();
    if (normalizedNextPage === null) {
      this._inFlightListByCacheKey.set(cacheKey, promise);
      promise.finally(() => {
        this._inFlightListByCacheKey.delete(cacheKey);
      });
    }

    return promise;
  }

  /**
   * @param {object|null|undefined} pag - `listPagination` snapshot
   * @returns {boolean} Whether another page can be requested (tolerates stringly-typed API flags).
   */
  _listPaginationHasNextForQueryFetch(pag) {
    if (!pag || pag.next_page === null || pag.next_page === undefined || pag.next_page === "") {
      return false;
    }
    return pag.has_next === true || pag.has_next === "true";
  }

  _logQueryDrivenCapReached() {
    if (this._queryDrivenLoggedStop) return;
    this._queryDrivenLoggedStop = true;
    const page = (this.listPagination?.page ?? 0) + 1;
    logPropertyFilter(
      `loaded page ${page}: no results. Filtered search stopped after ${QUERY_DRIVEN_AUTO_PAGE_MAX} auto-fetches.`
    );
  }

  _markQueryDrivenCapReached() {
    this._logQueryDrivenCapReached();
    this.queryDrivenAutoPaginationExhausted = true;
  }

  _markQueryDrivenFilteredSearchComplete() {
    this.queryDrivenAutoPaginationExhausted = true;
  }

  _kickQueryDrivenFetchIfNeeded() {
    const loadOptions = {
      role: this.queryDrivenLoadRole,
      userId: this.queryDrivenLoadUserId,
      fromQueryDrivenAutoPagination: true,
    };
    if (this.queryDrivenRevalidateAfterMs !== undefined) {
      loadOptions.revalidateAfterMs = this.queryDrivenRevalidateAfterMs;
    }
    void this.fetchNextPageForFilters(
      {
        query: this.queryDrivenSearchQuery,
        filterSignature: this.queryDrivenFilterSignature,
        hasActiveFilters: this.queryDrivenHasActiveFilters,
        filteredCount: this.queryDrivenFilteredCount,
      },
      loadOptions
    );
  }

  /**
   * Sync view-derived filter query state so MobX can re-run query-driven pagination after each load.
   *
   * @param {{
   *   query?: string,
   *   filterSignature?: string,
   *   hasActiveFilters?: boolean,
   *   filteredCount?: number,
   *   role?: unknown,
   *   userId?: string,
   *   revalidateAfterMs?: number
   * }} [params]
   */
  syncQueryDrivenAutoPaginationContext({
    query = "",
    filterSignature = "",
    hasActiveFilters = false,
    filteredCount = 0,
    role = null,
    userId = null,
    revalidateAfterMs,
  } = {}) {
    const q = typeof query === "string" ? query : "";
    const sig = typeof filterSignature === "string" ? filterSignature : "";
    const nextKey = `${q}\0${sig}`;
    const contextChanged = nextKey !== this._queryDrivenLastContextKey;
    if (contextChanged) {
      this._queryDrivenConsecutiveAutoFetches = 0;
      this._queryDrivenLoggedStop = false;
      this._queryDrivenLastContextKey = nextKey;
    }

    const hasMatches = typeof filteredCount === "number" && Number.isFinite(filteredCount) && filteredCount > 0;
    if (hasMatches) {
      const previousAutoFetches = this._queryDrivenConsecutiveAutoFetches;
      this._queryDrivenConsecutiveAutoFetches = 0;
      this._queryDrivenLoggedStop = false;
      if (previousAutoFetches > 0) {
        const page = (this.listPagination?.page ?? 0) + 1;
        logPropertyFilter(
          `auto-pagination counter reset to 0 on page ${page} after ${filteredCount} matches (was ${previousAutoFetches} auto-fetches).`
        );
      }
    }

    runInAction(() => {
      if (contextChanged || hasMatches) {
        this.queryDrivenAutoPaginationExhausted = false;
      }
      this.queryDrivenSearchQuery = q;
      this.queryDrivenFilterSignature = sig;
      this.queryDrivenHasActiveFilters = hasActiveFilters === true;
      this.queryDrivenFilteredCount =
        typeof filteredCount === "number" && Number.isFinite(filteredCount) ? filteredCount : 0;
      this.queryDrivenLoadRole = role ?? null;
      this.queryDrivenLoadUserId = userId ?? null;
      this.queryDrivenRevalidateAfterMs = revalidateAfterMs;

      const paginationSettled = !this.listLoading && !this.listLoadingMore && this.listPagination != null;
      const zeroMatchesWithNoNextPage =
        paginationSettled &&
        hasActiveFilters === true &&
        !hasMatches &&
        !this._listPaginationHasNextForQueryFetch(this.listPagination);
      if (zeroMatchesWithNoNextPage) {
        this._markQueryDrivenFilteredSearchComplete();
      } else if (
        paginationSettled &&
        hasActiveFilters === true &&
        !hasMatches &&
        this._listPaginationHasNextForQueryFetch(this.listPagination) &&
        this._queryDrivenConsecutiveAutoFetches < QUERY_DRIVEN_AUTO_PAGE_MAX
      ) {
        // Cold-open / in-flight sync can mark exhausted before pagination exists; reopen when more pages remain.
        this.queryDrivenAutoPaginationExhausted = false;
      }
    });

    this._kickQueryDrivenFetchIfNeeded();
  }

  /**
   * Subscribe to list/pagination observables so auto-fetch runs again after each page when the
   * client filter still matches nothing (React useEffect alone does not re-fire when length stays 0).
   * Call from PropertyList on mount; pair with {@link stopQueryDrivenAutoPaginationReaction} on unmount.
   */
  startQueryDrivenAutoPaginationReaction() {
    if (this._queryDrivenPaginationDisposer) return;

    this._queryDrivenPaginationDisposer = reaction(
      () => ({
        query: this.queryDrivenSearchQuery,
        filterSignature: this.queryDrivenFilterSignature,
        hasActiveFilters: this.queryDrivenHasActiveFilters,
        filteredCount: this.queryDrivenFilteredCount,
        listLoadingMore: this.listLoadingMore,
        listLoading: this.listLoading,
        listCustomer: this.listCustomer,
        listLen: this.listProperties.length,
        paginationRef: this.listPagination,
        nextPageToken: this.listPagination?.next_page,
        hasNextForFetch: this._listPaginationHasNextForQueryFetch(this.listPagination),
      }),
      () => {
        this._kickQueryDrivenFetchIfNeeded();
      }
    );
  }

  /** Tear down the reaction from {@link startQueryDrivenAutoPaginationReaction}. */
  stopQueryDrivenAutoPaginationReaction() {
    if (this._queryDrivenPaginationDisposer) {
      this._queryDrivenPaginationDisposer();
      this._queryDrivenPaginationDisposer = null;
    }
  }

  /**
   * Fetch the next page when client-side filtering yields zero matches and more server pages exist.
   *
   * @param {{ query?: string, filterSignature?: string, hasActiveFilters?: boolean, filteredCount?: number }} params
   * @param {{ role?: unknown, userId?: string, revalidateAfterMs?: number, fromQueryDrivenAutoPagination?: boolean }} [loadOptions]
   * @returns {Promise<Object|null>}
   */
  async fetchNextPageForFilters(
    { query = "", filterSignature = "", hasActiveFilters = false, filteredCount = 0 } = {},
    loadOptions = {}
  ) {
    const trimmedQuery = typeof query === "string" ? query.trim() : "";
    const hasQueryForSearch = trimmedQuery.length >= 2;
    const hasFilterSignature = typeof filterSignature === "string" && filterSignature.length > 0;

    if (!hasActiveFilters) return null;
    if (!hasQueryForSearch && !hasFilterSignature) return null;
    if (filteredCount > 0) return null;
    if (!this.listCustomer) return null;
    if (this.queryDrivenAutoPaginationExhausted) return null;

    const pag = this.listPagination;
    if (!this._listPaginationHasNextForQueryFetch(pag)) return null;
    if (this.listLoading || this.listLoadingMore) return null;

    const { role = null, userId = null, revalidateAfterMs, fromQueryDrivenAutoPagination = false } = loadOptions;

    if (fromQueryDrivenAutoPagination && this._queryDrivenConsecutiveAutoFetches >= QUERY_DRIVEN_AUTO_PAGE_MAX) {
      runInAction(() => {
        this._markQueryDrivenCapReached();
      });
      return null;
    }

    const result = await this.loadList({
      customer: this.listCustomer,
      nextPage: pag.next_page,
      ...(revalidateAfterMs !== undefined ? { revalidateAfterMs } : {}),
      role,
      userId,
    });

    // Count completed attempts (success or failure) so API errors cannot bypass the cap.
    if (fromQueryDrivenAutoPagination) {
      runInAction(() => {
        this._queryDrivenConsecutiveAutoFetches += 1;
        if (this._queryDrivenConsecutiveAutoFetches >= QUERY_DRIVEN_AUTO_PAGE_MAX) {
          this._markQueryDrivenCapReached();
        } else if (!this._listPaginationHasNextForQueryFetch(this.listPagination)) {
          this._markQueryDrivenFilteredSearchComplete();
        }
      });
    }

    return result;
  }

  /**
   * Same as {@link fetchNextPageForFilters} with `hasActiveFilters: true` (active search / filter bar mode).
   * Name reflects the common case (text query); filter-only signatures still use {@link fetchNextPageForFilters}.
   *
   * @param {string} query - Debounced search query from the filter bar
   * @param {number} filteredCount - Properties passing client-side filters
   * @param {{ role?: unknown, userId?: string, revalidateAfterMs?: number }} [loadOptions] - Passed through to `loadList` (GraphQL `user_role` / `user_id` when set)
   * @returns {Promise<Object|null>}
   */
  async fetchNextPageForQuery(query, filteredCount, loadOptions = {}) {
    return this.fetchNextPageForFilters(
      {
        query,
        hasActiveFilters: true,
        filteredCount,
      },
      loadOptions
    );
  }

  prependToList(newProperties = []) {
    if (!Array.isArray(newProperties) || newProperties.length === 0) return;

    runInAction(() => {
      const incoming = newProperties.filter((property) => property && typeof property === "object");
      if (incoming.length === 0) return;

      const incomingById = new Map();
      for (const property of incoming) {
        const propertyId = property?.id ?? property?.property_id;
        if (propertyId === undefined || propertyId === null || propertyId === "") continue;
        incomingById.set(String(propertyId), property);
      }

      if (incomingById.size === 0) return;

      const dedupedExisting = this.listProperties.filter((property) => {
        const propertyId = property?.id ?? property?.property_id;
        if (propertyId === undefined || propertyId === null || propertyId === "") return true;
        return !incomingById.has(String(propertyId));
      });

      this.listProperties = [...incomingById.values(), ...dedupedExisting];
    });
  }

  resolvePropertyForDisplay({ property } = {}) {
    if (!property || typeof property !== "object") return null;
    return property;
  }

  getDisplayProperty(params) {
    return this.resolvePropertyForDisplay(params);
  }

  applyPropertyPatchForCustomer(customer, propertyId, partialUpdate) {
    if (!customer || !propertyId || !partialUpdate || typeof partialUpdate !== "object") {
      return { patchedCurrentList: false, patchedCacheKeys: 0, patchedProperty: null };
    }

    const propertyIdString = String(propertyId);
    let patchedCurrentList = false;
    let patchedCacheKeys = 0;
    let patchedProperty = null;

    runInAction(() => {
      if (this.listCustomer === customer && Array.isArray(this.listProperties)) {
        const currentIndex = this.listProperties.findIndex((property) => String(property?.id) === propertyIdString);
        if (currentIndex >= 0) {
          const nextProperty = mergePropertyPatch(this.listProperties[currentIndex], partialUpdate);
          const nextList = [...this.listProperties];
          nextList[currentIndex] = nextProperty;
          this.listProperties = nextList;
          patchedCurrentList = true;
          patchedProperty = nextProperty;
        }
      }

      const exactListCacheKey = listCacheKey(customer);
      const customerPrefix = `${customer}::`;
      for (const [cacheKey, cacheEntry] of this.listCacheByKey.entries()) {
        if (cacheKey !== exactListCacheKey && !cacheKey.startsWith(customerPrefix)) continue;
        if (!Array.isArray(cacheEntry?.properties)) continue;

        const cacheIndex = cacheEntry.properties.findIndex((property) => String(property?.id) === propertyIdString);
        if (cacheIndex < 0) continue;

        const nextProperty = mergePropertyPatch(cacheEntry.properties[cacheIndex], partialUpdate);
        const nextProperties = [...cacheEntry.properties];
        nextProperties[cacheIndex] = nextProperty;

        this.listCacheByKey.set(cacheKey, {
          ...cacheEntry,
          properties: nextProperties,
        });

        patchedCacheKeys += 1;
        if (!patchedProperty) patchedProperty = nextProperty;
      }
    });

    return { patchedCurrentList, patchedCacheKeys, patchedProperty };
  }

  /**
   * Drops a property from the live list and every cached list page for this customer.
   *
   * @param {string} customer
   * @param {string} propertyId
   * @returns {{ removedCurrentList: boolean, removedCacheKeys: number }}
   */
  removePropertyForCustomer(customer, propertyId) {
    if (!customer || !propertyId) {
      return { removedCurrentList: false, removedCacheKeys: 0 };
    }

    const matchesProperty = (property) => {
      const id = property?.id ?? property?.property_id;
      if (id == null || id === "") return false;
      return cachedPropertyIdMatchesKey(id, propertyId) || cachedPropertyIdMatchesKey(propertyId, id);
    };
    const matchesIdToken = (token) =>
      cachedPropertyIdMatchesKey(token, propertyId) || cachedPropertyIdMatchesKey(propertyId, token);

    let removedCurrentList = false;
    let removedCacheKeys = 0;

    runInAction(() => {
      if (this.listCustomer === customer) {
        if (Array.isArray(this.listProperties)) {
          const nextList = this.listProperties.filter((property) => !matchesProperty(property));
          if (nextList.length !== this.listProperties.length) {
            this.listProperties = nextList;
            removedCurrentList = true;
          }
        }
        if (Array.isArray(this.listPropertyIds)) {
          const nextIds = this.listPropertyIds.filter((id) => !matchesIdToken(id));
          if (nextIds.length !== this.listPropertyIds.length) {
            this.listPropertyIds = nextIds;
            removedCurrentList = true;
          }
        }
      }

      const exactListCacheKey = listCacheKey(customer);
      const customerPrefix = `${customer}::`;
      for (const [cacheKey, cacheEntry] of this.listCacheByKey.entries()) {
        if (cacheKey !== exactListCacheKey && !cacheKey.startsWith(customerPrefix)) continue;
        if (!Array.isArray(cacheEntry?.properties)) continue;

        const nextProperties = cacheEntry.properties.filter((property) => !matchesProperty(property));
        if (nextProperties.length === cacheEntry.properties.length) continue;

        removedCacheKeys += 1;
        if (nextProperties.length === 0) {
          this.listCacheByKey.delete(cacheKey);
          continue;
        }

        const nextIds = Array.isArray(cacheEntry.propertyIds)
          ? cacheEntry.propertyIds.filter((id) => !matchesIdToken(id))
          : cacheEntry.propertyIds;

        this.listCacheByKey.set(cacheKey, {
          ...cacheEntry,
          properties: nextProperties,
          propertyIds: nextIds,
        });
      }
    });

    return { removedCurrentList, removedCacheKeys };
  }
}

export default PropertyListStore;
